<script>
    import { onMount, afterUpdate } from 'svelte';
	import { writable } from 'svelte/store';
	import { Circle2 } from 'svelte-loading-spinners';
    import { navigate, useLocation } from 'svelte-routing';
    import { currentUser } from '../../store/store.js';
	import TopBar from '../TopBar.svelte';
	import { handleGoogleLogin, handleLogin } from '../../utils/login.js';

    export let isOverlay = false;
    const location = useLocation();

    onMount(async () => {
		const query = new URLSearchParams($location.search);
		if (query.get('view') === 'signup') {
			createAccount = true;
		}

        if (!isOverlay) {
            usernameInput.focus(); // Focus the input element
            usernameInput.select();
        }
	});

	afterUpdate(() => {
		if (!createAccount) {
			const googleButton = document.getElementById('google-signin-button');
			if (window.google && googleButton && googleButton.innerHTML === '') {
				window.google.accounts.id.renderButton(
					googleButton,
					{ theme: 'outline', size: 'large', width: '310px' }
				);
			}
		}
	});

	// Create the script element
	const googleScript = document.createElement('script');
	googleScript.src = 'https://accounts.google.com/gsi/client';
	googleScript.async = true;
	googleScript.defer = true;

	// Attach the onload event
	googleScript.onload = () => {
		// Initialize Google One Tap
		window.google.accounts.id.initialize({
			client_id: '324523837899-0l8ak628ssr088mej9cv48thvqlcmsi5.apps.googleusercontent.com',
			callback: async (res) => {
				try {
					await handleGoogleLogin(res);
					if (!isOverlay) {
                        const from = $location.state?.from || '/';
                        if (from === '/profile') {
                            navigate('/');
                        } else {
						    navigate(from);
                        }
					}
				} catch (error) {
					errorMessage = error.message || 'Ett fel uppstod vid inloggning med Google.';
				}
			}
		});
		
		// Render the Google Sign-In button
		window.google.accounts.id.renderButton(
			document.getElementById('google-signin-button'),
			{ theme: 'outline', size: 'large', width: '310px' }
		);
	};

	// Append the script to the document head (or body)
	document.head.appendChild(googleScript);

	let loginLoading = writable(false); 

	let username = '';
	let usernameInput = null;
    let loginPassword = '';
	let errorMessage = '';
	let createAccount = false;
	let email = '';
	let password = '';
	let firstName = '';
	let lastName = '';
	let business = false;
	let organizationNumber = '';
	let passwordError = '';
	let emailError = '';
	let firstNameError = '';
	let lastNameError = '';
	let businessError = '';
	let organizationNumberError = '';
	let formError = '';
	let emailInput;
	let passwordInput;
	let firstNameInput;
	let lastNameInput;
	let organizationNumberInput;

    function toggleCreateAccount() {
		createAccount = !createAccount;
	}

    async function handleCreateAccount() {
		// Reset errors
		passwordError = '';
		emailError = '';
		firstNameError = '';
		lastNameError = '';
		businessError = false;
		organizationNumberError = '';
		formError = '';

		// Basic email validation
		if (!validateEmail(email)) {
			emailError = 'Ogiltig e-postadress.';
			return;
		}

		if (password.length < 8) {
			passwordError = 'Lösenordet måste vara minst 8 tecken långt.';
			return;
		}

		if (firstName.length <= 0) {
			firstNameError = 'Var vänlig ange ett förnamn.';
			return;
		}

		if (lastName.length <= 0) {
			lastNameError = 'Var vänlig ange ett efternamn.';
			return;
		}

		if (business !== false && business !== true) {
			businessError = 'Var vänlig att ange om du representerar ett företag eller ej.';
			return;
		}

		// Validate organization number if provided
		if (organizationNumber) {
			const formatted = formatOrganizationNumber(organizationNumber);
			const validation = validateOrganizationNumber(formatted);
			if (!validation.valid) {
				organizationNumberError = validation.error;
				return;
			}
			// Update organizationNumber with formatted version
			organizationNumber = formatted;
		}

		// Simulate account creation
		try {
			loginLoading.set(true);

			// The backend expects query parameters, not JSON body
			const url = new URL('https://boanalys-backend-324523837899.europe-north1.run.app/create_account');
			url.searchParams.append('username', email);
			url.searchParams.append('password', password);
			url.searchParams.append('first_name', firstName);
			url.searchParams.append('last_name', lastName);
			url.searchParams.append('business', business.toString());
			
			// Add organization number if provided
			if (organizationNumber) {
				url.searchParams.append('organisation_number', organizationNumber);
			}

			const response = await fetch(url.toString(), {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
				}
			});

			if (!response.ok) {
				const errorData = await response.json();
				if (response.status === 400) {
					// Username already exists
					emailError = errorData.detail || 'Användarnamnet finns redan.';
				} else {
					formError = errorData.detail || 'Ett fel uppstod vid skapandet av kontot.';
				}
				return;
			}

			const data = await response.json();
			console.log('Account created with email:', email);
			// Reset form
			email = '';
			password = '';
			firstName = '';
			lastName = '';
			business = false;
			organizationNumber = '';
			organizationNumberError = '';
			createAccount = false;
			alert('Konto skapat framgångsrikt!');
		} catch (error) {
			formError = 'Ett fel uppstod vid skapandet av kontot.';
			console.error('Error creating account:', error);
		} finally {
			loginLoading.set(false);
		}
	}

	function validateEmail(email) {
		const re = /\S+@\S+\.\S+/;
		return re.test(email);
	}

	// Function to validate Swedish organization number (organisationsnummer)
	function validateOrganizationNumber(orgNum) {
		if (!orgNum) return { valid: true }; // Empty is valid (optional field)
		
		// Remove any non-numeric characters except hyphen
		const cleaned = orgNum.replace(/[^\d-]/g, '');
		
		// Check if it's too short or too long
		if (cleaned.length < 10) {
			return { valid: false, error: 'Organisationsnumret är för kort. Det ska vara 10 siffror.' };
		}
		
		if (cleaned.length > 11) {
			return { valid: false, error: 'Organisationsnumret är för långt. Det ska vara 10 siffror.' };
		}
		
		// Check format: XXXXXX-XXXX or XXXXXXXXXX
		const pattern = /^(\d{6})-?(\d{4})$/;
		const match = cleaned.match(pattern);
		
		if (!match) {
			return { valid: false, error: 'Ogiltigt format. Använd format XXXXXX-XXXX eller 10 siffror.' };
		}
		
		const [, group1, group2] = match;
		const digits = group1 + group2;
		
		// Must be exactly 10 digits
		if (digits.length !== 10) {
			return { valid: false, error: 'Organisationsnumret måste innehålla exakt 10 siffror.' };
		}
		
		// Check Luhn algorithm for the last 10 digits
		let sum = 0;
		for (let i = 0; i < 10; i++) {
			let digit = parseInt(digits[i]);
			if (i % 2 === 0) { // Even positions (0-indexed)
				digit *= 2;
				if (digit > 9) digit -= 9;
			}
			sum += digit;
		}
		
		if (sum % 10 !== 0) {
			return { valid: false, error: 'Organisationsnumret är inte giltigt enligt kontrollsiffran.' };
		}
		
		return { valid: true };
	}

	// Function to format organization number
	function formatOrganizationNumber(orgNum) {
		if (!orgNum) return '';
		const cleaned = orgNum.replace(/[^\d]/g, '');
		if (cleaned.length === 10) {
			return `${cleaned.slice(0, 6)}-${cleaned.slice(6)}`;
		}
		return orgNum;
	}

	// Real-time validation function
	function validateOrgNumberRealTime() {
		if (!organizationNumber) {
			organizationNumberError = '';
			return;
		}

		const formatted = formatOrganizationNumber(organizationNumber);
		const validation = validateOrganizationNumber(formatted);
		
		if (!validation.valid) {
			organizationNumberError = validation.error;
		} else {
			organizationNumberError = '';
		}
	}

    async function handleThisLogin() {
		loginLoading.set(true);
		errorMessage = ''; // Reset the error message at the start of the function

		try {
			await handleLogin(username, loginPassword);
			if (!isOverlay) {
                const from = $location.state?.from || '/';
                if (from === '/profile') {
                    navigate('/');
                } else {
				    navigate(from);
                }
			}
		} catch (error) {
			errorMessage = error.message || 'Ett fel uppstod vid inloggning.'; // Set the error message
		} finally {
			loginLoading.set(false);
		}
	}

    function parseJwt(token) {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			}).join(''));

			return JSON.parse(jsonPayload);
		} catch (e) {
			return null;
		}
	}

</script>

<style>
	.optional-label {
		font-size: 0.8em;
		color: #6c757d;
		font-weight: normal;
		font-style: italic;
	}

	.login-input.error {
		border-color: #dc3545 !important;
		box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1);
	}

	.login-input.error:focus {
		border-color: #dc3545 !important;
		box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
	}

	.error-message.org-error {
		display: flex;
		align-items: flex-start;
		gap: 5px;
		font-size: 0.85em;
		margin-top: 5px;
		line-height: 1.4;
	}

	.org-description {
		font-size: 0.8em;
		color: #6c757d;
		margin: 5px 0 15px 0;
		line-height: 1.4;
		font-style: italic;
	}

	.org-info {
		margin-bottom: 15px;
	}

	.org-consent {
		font-size: 0.8em;
		color: #dc3545;
		margin: 8px 0;
		line-height: 1.4;
		padding: 8px;
		background-color: #f8f9fa;
		border-left: 3px solid #dc3545;
		border-radius: 4px;
	}

	.org-terms {
		font-size: 0.8em;
		color: #6c757d;
		margin: 8px 0;
		line-height: 1.4;
	}

	.org-terms a {
		color: #007bff;
		text-decoration: none;
	}

	.org-terms a:hover {
		text-decoration: underline;
	}

	.discrete-hr {
		border: 0;
		height: 1px;
		background-color: #e0e0e0;
		margin: 15px 0;
	}

    .login-box.overlay {
        padding: 20px;
        background-color: #fff;
        border-radius: 10px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
        max-width: 400px;
    }
</style>


{#if !$currentUser?.accessToken}
{#if !createAccount}
<div class="login-box" class:full-page={!isOverlay} class:overlay={isOverlay}>

	<form on:submit|preventDefault={handleThisLogin} class="custom-form">
		<h3>Logga in</h3>
		<div class="google-btn-outer-wrapper">
			<div class="google-btn-inner-wrapper">
				<div id="google-signin-button"></div>
			</div>
		</div>
		<h4>Användarnamn</h4>
		<input type="text" class="login-input" bind:value={username} bind:this={usernameInput}>
		<h4>Lösenord</h4>
		<input type="password" class="login-input" bind:value={loginPassword}>
		
		{#if $loginLoading}
		<div class="login-loading">
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
		{:else}
		<p></p>
		<button type="submit" class="login-button" disabled={$loginLoading}>Logga in</button>
		{/if}
	
		{#if errorMessage}
		<p class="error-message">{errorMessage}</p>
		{/if}
	</form>
	<span 
		class="create-account-text" 
		role="button" 
		tabindex="0"
		on:click={toggleCreateAccount}
		on:keydown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				toggleCreateAccount();
			}
		}}
	>
		<h4>Skapa konto</h4>
	</span>

</div>
{:else}
<div class="login-box create-account-box" class:full-page={!isOverlay} class:overlay={isOverlay}>
	<form on:submit|preventDefault={handleCreateAccount} class="create-account-form">
	<h3>Skapa konto</h3>
	<h4>E-postadress</h4>
	<input
		type="email"
		bind:value={email}
		bind:this={emailInput}
		class="login-input"
		required
	/>
	{#if emailError}
		<div class="error-message">{emailError}</div>
	{/if}
	<h4>Lösenord</h4>
	<input
		type="password"
		bind:value={password}
		bind:this={passwordInput}
		class="login-input"
		required
	/>
	{#if passwordError}
		<div class="error-message">{passwordError}</div>
	{/if}
	<h4>Förnamn</h4>
	<input
		type="first-name"
		bind:value={firstName}
		bind:this={firstNameInput}
		class="login-input"
		required
	/>
	{#if firstNameError}
		<div class="error-message">{firstNameError}</div>
	{/if}
	<h4>Efternamn</h4>
	<input
		type="last-name"
		bind:value={lastName}
		bind:this={lastNameInput}
		class="login-input"
		required
	/>
	{#if lastNameError}
		<div class="error-message">{lastNameError}</div>
	{/if}
	<h4>Organisationsnummer <span class="optional-label">(valfritt)</span></h4>
	<input
		type="text"
		bind:value={organizationNumber}
		bind:this={organizationNumberInput}
		class="login-input"
		class:error={organizationNumberError}
		placeholder="XXXXXX-XXXX"
		maxlength="11"
		on:input={validateOrgNumberRealTime}
		on:blur={() => {
			// Format when user leaves the field
			if (organizationNumber) {
				organizationNumber = formatOrganizationNumber(organizationNumber);
				validateOrgNumberRealTime();
			}
		}}
	/>
	{#if organizationNumberError}
		<div class="error-message org-error"><i class="fa-solid fa-triangle-exclamation fa-sm icon-detail"></i> {organizationNumberError}</div>
	{/if}
	<div class="org-info">
		<p class="org-description">Du kan fylla i organisationsnumret i efterhand om du inte känner till det just nu.</p>
		{#if organizationNumber}
		<p class="org-consent"><strong>Viktigt:</strong> Genom att ange ett organisationsnummer bekräftar du på heder och samvete att du är medlem i den angivna bostadsrättsföreningen.</p>
		{/if}
			</div>
	{#if businessError}
		<div class="error-message">{businessError}</div>
	{/if}
	<!--
	<div class="checkbox-container">
		<input
		id="businessCheckbox"
		type="checkbox"
		class="login-checkbox"
		bind:checked={business}
		/>
		<h4 style="padding-left: 10px;">Företag</h4>
	</div>
	-->
	
	<hr class="discrete-hr">
	<p class="org-terms">Genom att skapa ett konto godkänner du våra <a href="/legal" target="_blank">användarvillkor</a>.</p>

	{#if formError}
		<div class="error-message">{formError}</div>
	{/if}
		{#if $loginLoading}
		<div class="login-loading">
		<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
		{:else}
		<p></p>
		<button type="submit" class="login-button" disabled={$loginLoading}>
			Skapa konto
		</button>
		<span 
			class="create-account-text" 
			role="button" 
			tabindex="0"
			on:click={toggleCreateAccount}
			on:keydown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggleCreateAccount();
				}
			}}
		>
			<h4>Tillbaka</h4>
		</span>
		{/if}
	</form>
	</div>
{/if}
{/if}

